import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { Dialog, DialogPanel, DialogTitle, Button } from '@headlessui/react'
import { toast } from 'react-hot-toast'

import { useCheckout } from '@/credits/api'

const CREDIT_PRICE = 0.5

export const CheckoutDialog = ({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (open: boolean) => void }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [credits, setCredits] = useState(10)

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    if (query.get('checkout') === 'success') {
      toast('Checkout successful', {
        icon: '✅',
      })
      navigate(
        {
          search: '',
        },
        { replace: true },
      )
    } else if (query.get('checkout') === 'cancel') {
      toast('Checkout canceled', {
        icon: '❌',
      })
      navigate(
        {
          search: '',
        },
        { replace: true },
      )
    }
  }, [location, navigate])

  const { mutateAsync: checkout } = useCheckout()

  const price = credits * CREDIT_PRICE

  const handleCheckout = () => {
    checkout({ amount: credits }).then(({ url }) => {
      window.location.href = url
    })
    setIsOpen(false)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      as="div"
      className="relative z-10 text-white focus:outline-none"
    >
      <div className="fixed inset-0 z-10 w-screen">
        <div className="flex h-screen items-center justify-center p-4">
          <DialogPanel
            transition
            className="flex h-[85%] w-full max-w-3xl flex-col gap-4 overflow-y-auto rounded-xl bg-white/5 p-6 backdrop-blur-2xl duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
          >
            <DialogTitle className="grow text-2xl font-semibold">Buy credits</DialogTitle>
            <div className="flex grow flex-col items-center justify-between gap-2">
              <div className="flex flex-col gap-2">
                <p className="font-thin">Select amount of credits to buy</p>
                <div className="flex items-center gap-2">
                  <Button
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 px-6 py-2 text-lg font-semibold text-white shadow-inner shadow-white/10 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-white/20 data-open:bg-white/10"
                    disabled={credits <= 10}
                    onClick={() => {
                      setCredits((credits) => {
                        const newCredits = credits - 10
                        if (newCredits < 10) {
                          return 10
                        }
                        return newCredits
                      })
                    }}
                  >
                    -
                  </Button>
                  <span className="w-32 rounded-md bg-white/10 px-6 py-2 text-center text-5xl font-semibold text-white shadow-inner shadow-white/10">
                    {credits}
                  </span>
                  <Button
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 px-6 py-2 text-lg font-semibold text-white shadow-inner shadow-white/10 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-white/20 data-open:bg-white/10"
                    onClick={() => {
                      setCredits((credits) => {
                        const newCredits = credits + 10
                        if (newCredits > 100) {
                          return 100
                        }
                        return newCredits
                      })
                    }}
                    disabled={credits >= 100}
                  >
                    +
                  </Button>
                </div>

                <p className="rounded-md bg-white/10 px-6 py-2 text-lg font-thin shadow-inner shadow-white/10">
                  Amount to pay {price.toFixed(2)}$
                </p>
              </div>
              <div className="flex justify-center gap-2">
                <Button
                  onClick={() => setIsOpen(false)}
                  className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-white/20 data-open:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCheckout}
                  className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-green-700 data-open:bg-green-600"
                >
                  Buy now
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
